import { Api } from '../typings/api';
import { DRIVE_ITEM_LIST_PAGE_SIZE, ListDisplayType } from '../containers/DriveItemList/constants';
import { DownloadStatus, OfflineManagerState } from '../containers/OfflineManager/reducer';
import Utils, { defaultFolderIconFolder } from '.';
import UserAgentUtils from './userAgent';
import UrlUtils from './url';
import DownloadManagerUtils from './downloadManager';
import { DownloadManagerState } from '../containers/DownloadManager/reducer';
import { DriveItemInfo, DriveItemInfoMap, DriveListState } from '../containers/DriveItemList/reducer';
import { DriveItemListOrigin } from '../containers/DriveItemListPage/constants';
import { ShareOrigin } from '../containers/Share/actions';
import { ReadItemsListState } from '../containers/ReadStatus/reducer';
import store from '../store';
import { AppType } from '../containers/App/reducer';
import { RouterParamsState } from '../containers/SetRouteParams/reducer';
import RouteUtils from './routes';
import { StoreState } from '../reducers';
import { CreateButtonOption, MenuItem, MenuItemType } from '../typings/menu';
import { ContextualMenuButtons } from '../components/ContextualMenu';
import { ContextualMenuItemType } from '../components/ContextualMenu/constants';
import { getIsAccountManagerOrAdmin } from '../containers/ProfilePage/selectors';
import PublicationFormat = Api.PublicationFormat;

const I18n = require('react-redux-i18n').I18n;

const minimumWidthRow = 360;
const minimumWidth = 720;
const maxOriginalImageSize = 2048;

enum ResolutionDisplayType {
	Card = 'Card',
	RowItem = 'RowItem',
	Detail = 'Detail',
	Theater = 'Theater',
	Coverflow = 'Coverflow'
}

enum ActionButton {
	Read = 'read',
	Download = 'download',
	Update = 'update',
	Delete = 'delete',
	Cancel = 'cancel',
	Required = 'required'
}

enum ActionButtonStyle {
	Bar = 'Bar',
	Icon = 'Icon',
	ParametersPage = 'ParametersPage'
}


interface ThumbnailDescriptor {
	isImage: boolean;
	src: string;
	fallbackSrc?: string;
}

class DriveItemUtils {

	static imageExtensions: string[] = ['.webp', '.jpg', '.jpeg', '.png', '.gif'];
	static imageWithSvgExtensions: string[] = [...DriveItemUtils.imageExtensions, '.svg'];
	// it's possible to set a pdf as image (e.g., in editor); conversion is done on server
	static imageWithPdfExtensions: string[] = [...DriveItemUtils.imageWithSvgExtensions, '.pdf'];
	static imageWithGifExtensions: string[] = [...DriveItemUtils.imageWithSvgExtensions, '.gif'];
	static videoExtensions: string[] = ['.mp4', '.webm', '.avi', '.mov'];
	static publicationExtensions: string[] = ['.pdf', '.epub', '.ppt', '.pptx', '.doc', '.docx', '.otp', '.odt'];
	static componentExtensions: string[] = ['.zip'];

	static getDisplayName(driveItem: Api.DriveItem): string {
		const { driveList, galleryInfos: { config } } = store.getState();
		const { id, titles, label } = driveItem;
		const multiAppLogin = DriveItemUtils.getMultiAppLogin();

		if (id === Utils.getSharedDirId(driveList, config)) {
			return I18n.t('messages.shared_with_me');
		}

		if (id === Utils.getArchiveDirId(driveList, config)) {
			return I18n.t('messages.archive_noun');
		}

		if (id === Utils.getSourcesDirId(driveList, config)) {
			return I18n.t('messages.sources');
		}

		if (multiAppLogin && id === Utils.getPrivateDirId(driveList, config) ) {
			const specialPrivateDirIds = config.specialPrivateDirIds;
			const isSpecialPrivateDir = specialPrivateDirIds && specialPrivateDirIds.includes(id);
			if (!isSpecialPrivateDir) {
				return I18n.t('messages.my_documents');
			}
		}

		const titleKeys = Object.keys(titles);
		return !!titleKeys.length ? titles[titleKeys[0]] : label;
	}

	static getMultiAppLogin(): string | null {
		const { multiAppAuthenticationInfos, appInfos } = store.getState();
		const { credentials } = multiAppAuthenticationInfos;
		const { type } = appInfos;

		if (type === AppType.Multiple && credentials && credentials.login) {
			return credentials.login;
		}

		return null;
	}

	static getLocale(driveItem: Api.DriveItem): string {
		const { titles, descriptions } = driveItem;
		const titleKeys = Object.keys(titles);
		const descriptionKeys = Object.keys(descriptions);
		return titleKeys[0] || descriptionKeys[0] || '';
	}

	static getExtension(driveItem: Api.DriveItem, isIcon: boolean = false): string {
		if (isIcon) {
			return Utils.getFileExtension(driveItem.icon);
		}

		switch (driveItem.type) {
			case Api.DriveItemType.Directory:
			case Api.DriveItemType.Gallery:
				return '';
			case Api.DriveItemType.Publication:
				return I18n.t(`messages.extension_flip`);
			case Api.DriveItemType.ExternalLink:
				return I18n.t(`messages.extension_external_link`);
		}

		if (driveItem.originalFile) {
			return Utils.getFileExtension(driveItem.originalFile);
		}

		return '';
	}

	static isPreviewable(driveItem: Api.DriveItem): boolean {
		return [Api.DriveItemType.Image, Api.DriveItemType.Movie, Api.DriveItemType.Sound].includes(driveItem.type);
	}

	static getThumbnailDescriptor = (
		driveItem: Api.DriveItem,
		isBox: boolean,
		offlineManager: OfflineManagerState,
		config: Api.Config,
		resolutions: { [k: string]: Api.ResolutionGroup},
		displayType?: ListDisplayType
	): ThumbnailDescriptor => {
		if (UserAgentUtils.isMobileApp) {
			const { thumbMap } = offlineManager;
			const driveItemStatus = thumbMap[driveItem.id];
			if (driveItemStatus && driveItemStatus.status === DownloadStatus.DOWNLOADED) {
				// Use local thumbnail if downloaded
				const src = Utils.getDeviceThumbnailPath(config, thumbMap, driveItem, resolutions, false);
				return { isImage: true, src };
			} else if (DriveItemUtils.hasIcon(driveItem)) {
				// Fallback to online thumbnail if not downloaded yet but has icon
				const src = DriveItemUtils.getThumbnail(driveItem, config, resolutions, isBox, displayType);
				const { icon } = driveItem;
				let fallbackSrc = '';

				if (icon) {
					fallbackSrc = UrlUtils.getUrlFromRelUrlWithMediaParam(icon, config);
				}

				return { isImage: true, src, fallbackSrc };
			}
		} else if (DriveItemUtils.hasIcon(driveItem)) {
			const src = DriveItemUtils.getThumbnail(driveItem, config, resolutions, isBox, displayType);
			const { icon } = driveItem;
			let fallbackSrc = '';

			if (icon) {
				fallbackSrc = UrlUtils.getUrlFromRelUrlWithMediaParam(icon, config);
			}

			return { isImage: true, src, fallbackSrc };
		}

		if (driveItem.type === Api.DriveItemType.Directory) {
			const defaultFolderIcon = DriveItemUtils.getDefaultFolderIcon();

			if (defaultFolderIcon) {
				const src = DriveItemUtils.getDefaultFolderIconPath(defaultFolderIcon);
				return { isImage: true, src };
			}
		}

		return { isImage: false, src: DriveItemUtils.getDefaultThumbnail(driveItem) };
	}

	static getThumbnail(
		driveItem: Api.DriveFolderInfo,
		galleryConfig: Api.Config,
		globalResolutions: { [k: string]: Api.ResolutionGroup },
		isBox: boolean,
		displayType?: ListDisplayType
	): string {
		// fallback order :
		// itemOriginalUrl (if same as icon and not too big) > iconWithResolution > iconOriginalUrl > defaultIcon
		const { driveList: { driveItems } } = store.getState();
		const resolutions = driveItem.resolutions || globalResolutions;
		const { icon } = driveItem;

		if (icon) {
			const iconId = this.getIconId(driveItem);
			const iconItem = driveItems[iconId] ? driveItems[iconId] : driveItem;

			// If iconId matches the driveItem's own ID (self-reference), use the icon path directly
			// This prevents videos with icon="$media$ID.webp" from falling back to the video's .mp4 file
			const isSelfReferencingIcon = iconId === driveItem.id && iconId > 0;

			// Check if icon path ends with .webp (or other image extensions) - indicates it's a thumbnail image, not the video file
			const iconExtension = Utils.getFileExtension(icon);
			const isImageIcon = DriveItemUtils.imageExtensions.includes(`.${iconExtension.toLowerCase()}`);
			const iconItemNotFound = iconItem === driveItem && iconId > 0 && iconId !== driveItem.id;

			if (!DriveItemUtils.needsResolutions(driveItem)) {
				return UrlUtils.getUrlFromRelUrlWithMediaParam(icon, galleryConfig);
			}

			const currentDisplayType = displayType && displayType === ListDisplayType.Row ? ResolutionDisplayType.RowItem : ResolutionDisplayType.Card;

			if (!DriveItemUtils.hasIconMetadata(driveItem, resolutions) || !driveItem.associatedResolutions) {
				// When iconItem is the same as driveItem (self-reference) and the icon path has an image extension,
				// use the icon path directly instead of falling back to originalFile
				// This prevents issues where:
				// - Videos with icon="$media$ID.webp" fall back to .mp4 file
				// - Publications with icon="$media$ID/path/thumb.jpg" fall back to .epub file
				// - Images with icon="$media$ID.webp" fall back to wrong file
				// Also handle cases where icon references another item that doesn't exist in driveItems
				if ((isSelfReferencingIcon && isImageIcon) || (iconItemNotFound && isImageIcon)) {
					return UrlUtils.getUrlFromRelUrlWithMediaParam(icon, galleryConfig);
				}
				return UrlUtils.getItemOriginalFileUrl(galleryConfig, iconItem.originalFile);
			} else if (DriveItemUtils.hasIconMetadata(driveItem, resolutions)) {
				const availableResolutionsId = driveItem.associatedResolutions[iconId] || 'default';
				const availableResolutions = resolutions[availableResolutionsId];
				const resolution = DriveItemUtils.getIdealSizeFromResolutions(availableResolutions, currentDisplayType, isBox);

				return this.getImagePathByResolution(driveItem, galleryConfig, resolution, undefined, true);
			} else {
				return this.getDefaultThumbnail(driveItem);
			}
		}

		return this.getDefaultThumbnail(driveItem);
	}

	static needsResolutions(driveItem: Api.DriveFolderInfo): boolean {
		const { driveList: { driveItems } } = store.getState();
		const iconId = this.getIconId(driveItem);
		const iconItem = driveItems[iconId] ? driveItems[iconId] : driveItem;

		// For items with self-referencing icons that have image extensions, don't use resolutions
		// This ensures we use the icon path directly instead of falling back to originalFile
		// Examples:
		// - Videos: icon="$media$ID.webp" should use .webp, not .mp4
		// - Publications: icon="$media$ID/path/thumb.jpg" should use .jpg, not .epub
		// - Images: icon="$media$ID.webp" should use .webp, not original file
		const isSelfReferencingIcon = iconId === driveItem.id && iconId > 0;
		if (isSelfReferencingIcon && driveItem.icon) {
			const iconExtension = Utils.getFileExtension(driveItem.icon);
			const isImageIcon = DriveItemUtils.imageExtensions.includes(`.${iconExtension.toLowerCase()}`);
			if (isImageIcon) {
				return false;
			}
		}

		return !(iconId < 0 || DriveItemUtils.shouldUseOriginalThumb(iconItem));
	}

	static hasIconMetadata(driveItem: Api.DriveFolderInfo, globalResolutions: { [k: string]: Api.ResolutionGroup } | undefined = undefined): boolean {
		const { icon, associatedResolutions, resolutions: driveItemResolutions } = driveItem;
		const resolutions = globalResolutions || driveItemResolutions;

		if (icon && associatedResolutions && resolutions) {
			const iconId = this.getIconId(driveItem);
			const availableResolutionsId = associatedResolutions[iconId] || 'default';
			const availableResolutions = resolutions[availableResolutionsId];

			if (icon && !availableResolutions) {
				console.warn('Icon not found in available resolutions', driveItem);
			}

			return availableResolutions !== undefined && DriveItemUtils.isSupportedExtension(driveItem, resolutions) && driveItem.type !== Api.DriveItemType.Movie;
		}

		return false;
	}

	static getTheaterImage(driveItem: Api.DriveFolderInfo, galleryConfig: Api.Config, globalResolutions: { [k: string]: Api.ResolutionGroup }): string {
		const resolutions = driveItem.resolutions || globalResolutions;

		const availableResolutionsId = driveItem.associatedResolutions && driveItem.associatedResolutions[driveItem.id] || 'default';
		const availableResolutions = resolutions[availableResolutionsId];

		if (DriveItemUtils.shouldUseOriginalFile(driveItem, ResolutionDisplayType.Theater) || availableResolutions === undefined ) {
			return UrlUtils.getItemOriginalFileUrl(galleryConfig, driveItem.originalFile);
		}

		const resolution = DriveItemUtils.getIdealSizeFromResolutions(availableResolutions, ResolutionDisplayType.Theater, true);
		return this.getImagePathByResolution(driveItem, galleryConfig, resolution, false);
	}

	static isPreferredExtension(extension: string) {
		const isSvg = extension === 'svg';
		const isGif = extension === 'gif';

		return isSvg || isGif;
	}

	static shouldUseOriginalFile(driveItem: Api.DriveItem, resolutionDisplayType: ResolutionDisplayType): boolean {
		const imageWidth = parseInt(DriveItemUtils.getImageWidth(driveItem), 10);
		const targetWidth = DriveItemUtils.getTargetWidth(resolutionDisplayType);
		const extension = Utils.getFileExtension(driveItem.originalFile);
		return !driveItem.associatedResolutions || imageWidth < Math.max(targetWidth, maxOriginalImageSize) || DriveItemUtils.isPreferredExtension(extension);
	}

	static shouldUseOriginalThumb(driveItem: Api.DriveItem): boolean {
		const extension = driveItem.icon ? Utils.getFileExtension(driveItem.icon) : '';
		return DriveItemUtils.isPreferredExtension(extension);
	}

	static hasIcon(driveItem?: Api.DriveItem): boolean {
		return !!(driveItem && driveItem.icon);
	}

	static getIconId(driveItem: Api.DriveItem): number {
		if (!driveItem.icon) {
			return -1;
		}
		// Match both patterns: $media$123.ext and $media$123/path.ext
		const regexMatches = /^\$media\$([0-9]+)(?:\..*|\/.*)$/.exec( driveItem.icon );
		return regexMatches !== null
			? parseInt(regexMatches[1], 10)
			: -1;
	}

	static isSupportedExtension(driveItem: Api.DriveItem, resolutions: { [k: string]: Api.ResolutionGroup}): boolean {
		const iconId = this.getIconId(driveItem);
		const resolvedResolution = driveItem.associatedResolutions ? resolutions[driveItem.associatedResolutions[iconId]] : resolutions.default;
		if (resolvedResolution) {
			const supportedExtensions = resolvedResolution.supported;
			const iconExtension = DriveItemUtils.getIconExtension(driveItem);
			return supportedExtensions.includes(iconExtension);
		}
		return false;
	}

	static getIconExtension(driveItem: Api.DriveItem): string {
		if (driveItem.icon) {
			const extensionRegex = /(?:\.([^.]+))?$/;
			const regexMatch = extensionRegex.exec(driveItem.icon);
			return !!regexMatch && !!regexMatch[1] ? regexMatch[1] : '';
		}
		return '';
	}

	static getDownloadedFileName(driveItem: Api.DriveItem): string {
		const extension = Utils.getFileExtension(driveItem.originalFile);
		let fileName;

		if (extension === 'tif') {
			fileName = `${driveItem.id}.jpg`;
		} else {
			fileName = `${driveItem.id}.${extension}`;
		}

		return fileName;
	}

	static getTargetWidth(type: ResolutionDisplayType): number {
		switch (type) {
			case ResolutionDisplayType.Theater:
				return Math.max(window.innerHeight, window.innerWidth);
			case ResolutionDisplayType.RowItem:
				return minimumWidthRow;
			default:
				return minimumWidth;
		}
	}

	static getImageWidth(driveItem: Api.DriveItem): string {
		if (driveItem.resolution) {
			const widthRegex = /(\d+)x/;
			const regexMatch = widthRegex.exec(driveItem.resolution);
			return !!regexMatch && !!regexMatch[1] ? regexMatch[1] : '';
		}

		return '';
	}

	static getIdealSizeFromResolutions(availableResolutions: Api.ResolutionGroup, type: ResolutionDisplayType, isBox: boolean): Api.ResolutionSize {
		const targetWidth = DriveItemUtils.getTargetWidth(type);

		// Try to find an associated box that fits
		let resolvedSize = this.getIdealSizeFromSizes(availableResolutions.sizes, targetWidth, isBox);

		// If no box fit, we ignore the isBox parameter
		resolvedSize = resolvedSize || this.getIdealSizeFromSizes(availableResolutions.sizes, targetWidth);

		// If still no box fit, we use the biggest resolution
		if (!resolvedSize) {
			for (const metaSize of availableResolutions.sizes) {
				if (!resolvedSize || metaSize.width > resolvedSize.width) {
					resolvedSize = metaSize;
				}
			}
		}

		return <Api.ResolutionSize> resolvedSize;
	}

	static getIdealSizeFromSizes(metaSizes: Api.ResolutionSize[], targetWidth: number, isBox?: boolean): Api.ResolutionSize | undefined {
		let resolvedSize: Api.ResolutionSize | undefined;

		for (const metaSize of metaSizes) {
			if ((typeof isBox === 'undefined' || metaSize.box === isBox)
			&& (metaSize.width >= targetWidth && (!resolvedSize || metaSize.width < resolvedSize.width))) {
				resolvedSize = metaSize;
			}
		}
		return resolvedSize;
	}

	static getImagePathByResolution(driveItem: Api.DriveItem, galleryConfig: Api.Config, size: Api.ResolutionSize, isThumb: boolean = true, isIcon: boolean = false): string {
		const extension = size.extension || (driveItem.type === Api.DriveItemType.Image ? this.getExtension(driveItem, isIcon) : 'jpg');
		return UrlUtils.getImageUrl(galleryConfig, driveItem, size, extension, isThumb);
	}

	static getIconOriginalName(driveItem: Api.DriveItem): string {
		if (!driveItem.icon) {
			return '';
		}

		return this.getAssetOriginalName(driveItem.icon);
	}

	static getAssetOriginalName(ressourceUrl: string): string {
		const regexMatches = /^\$media\$(.+)$/.exec(ressourceUrl );
		return regexMatches !== null
			? regexMatches[1]
			: '';
	}


	static getAssetTypeTranslationKey(assetType: Api.AssetType, publicationFormat?: Api.PublicationFormat | undefined): string {
		if (assetType === Api.AdditionalItemType.Note) {
			return 'messages.note';
		}

		const isProposal = assetType === Api.DriveItemType.Publication
			&& DriveItemUtils.isProposal(publicationFormat);
		if (isProposal) {
			return 'fileFormat.PROPOSAL';
		}

		return `type.asp-type-${assetType}`;
	}

	static getIconOriginalPath(driveItem: Api.DriveItem, galleryConfig: Api.Config): string {
		return this.getAssetOriginalPath(galleryConfig, this.getIconOriginalName(driveItem));
	}

	static getAssetOriginalPath(galleryConfig: Api.Config, ressourceName: string) {
		return UrlUtils.getItemOriginalFileUrl(galleryConfig, ressourceName);
	}

	static getDefaultFolderIcon(): string | undefined {
		const {
			galleryInfos: { config: { applicationGallery } },
			driveList: { driveItems },
		} = store.getState();
		const { defaultCover } = driveItems[applicationGallery];

		return defaultCover;
	}

	static getDefaultFolderIconPath(icon: string): string {
		const { galleryInfos: { config }, offlineManager } = store.getState();
		let src = '';

		if (UserAgentUtils.isMobileApp) {
			// Check if the default folder icon has been downloaded
			const { defaultFolderIcon: { status } } = offlineManager;
			if (status === DownloadStatus.DOWNLOADED) {
				// Use local file path
				src = `${Utils.getThumbnailsFolder()}/${defaultFolderIconFolder}/${icon.replace(UrlUtils.mediaParamRegex, '')}`;
			} else {
				// Fallback to online URL until download completes
				src = UrlUtils.getUrlFromRelUrlWithMediaParam(icon, config);
			}
		} else {
			src = UrlUtils.getUrlFromRelUrlWithMediaParam(icon, config);
		}

		return src;
	}

	static getFolderBgMediaPath(driveItemId: number, bgMedia: string): string {
		const { galleryInfos: { config } } = store.getState();
		let src = '';

		if (UserAgentUtils.isMobileApp) {
			const extension = Utils.getFileExtension(bgMedia);
			src = `${Utils.getBgMediaFolder()}/${driveItemId}/background.${extension}`;
		} else {
			src = UrlUtils.getUrlFromRelUrlWithMediaParam(bgMedia, config);
		}

		return src;
	}

	static getDefaultThumbnail(driveItem: Api.DriveItem): string {
		switch (driveItem.type) {
			case Api.DriveItemType.Directory:
				return 'iconset_doc_default';
			case Api.DriveItemType.Publication:
				return 'iconset_publication';
			case Api.DriveItemType.File:
				return 'iconset_file';
			case Api.DriveItemType.Image:
				return 'iconset_image-bold';
			case Api.DriveItemType.Movie:
				return 'iconset_video-bold';
			case Api.DriveItemType.Sound:
				return 'iconset_sound-bold';
			case Api.DriveItemType.Link:
			case Api.DriveItemType.ExternalLink:
				return 'iconset_link';
			case Api.DriveItemType.Gallery:
				return 'iconset_gallery-bold';
			default:
				return 'iconset_page-bold';
		}
	}

	static getDescription(driveItem: Api.DriveItem, { locale }: Api.Template): string {
		const descriptionKeys = Object.keys(driveItem.descriptions);

		if (!!descriptionKeys.length) {
			if (driveItem.descriptions[locale]) {
				return driveItem.descriptions[locale];
			}
			return driveItem.descriptions[descriptionKeys[0]];
		}
		return '';
	}

	static getPublicationCover(path: string, page: number = 1): string {
		return path.replace(/{page}/, page.toString());
	}

	static isStored(itemId: number, storage: number[]): boolean {
		return storage.includes(itemId);
	}

	static getPageByDriveItemId(ressources: Api.DriveItemResource[], driveItemId: number): number {
		for (let i = 0; i < ressources.length; i++) {
			if (driveItemId === ressources[i].id) {
				return ( 1 + Math.floor( i / DRIVE_ITEM_LIST_PAGE_SIZE ) );
			}
		}
		return -1;
	}

	static getShareTitle(sharedItems: Api.DriveItem[]) {
		const firstItem = sharedItems[0];

		if (sharedItems.length > 1) {
			return I18n.t('messages.selection');
		}

		return DriveItemUtils.getDisplayName(firstItem);
	}

	static getShareLink(sharedItems: Api.DriveItem[], galleryConfig: Api.Config, shareOrigin: ShareOrigin) {

		if (sharedItems.length > 1) {
			return UrlUtils.getUrlDriveItemListPage(galleryConfig, DriveItemListOrigin.Assortment, sharedItems.map(driveItem => driveItem.id).join(), true);
		}

		const firstItem = sharedItems[0];
		if (firstItem.type === Api.DriveItemType.Directory) {
			return UrlUtils.getUrlDriveItemListPage(galleryConfig, DriveItemListOrigin.Folder, firstItem.id.toString(), true);
		}

		switch (shareOrigin) {
			case ShareOrigin.Detail:
				return UrlUtils.getUrlDriveItemDetailPage(galleryConfig, firstItem.id, undefined, true);
			case ShareOrigin.Selection:
				return UrlUtils.getUrlDriveItemListPage(galleryConfig, DriveItemListOrigin.Assortment, sharedItems[0].id.toString(), true);
			case ShareOrigin.Theater:
			default:
				return UrlUtils.getUrlTheater(galleryConfig, DriveItemListOrigin.Folder, DriveItemUtils.getParentFolderId(firstItem).toString(), firstItem.id, true);
		}
	}

	static containsString(driveItem: Api.DriveItem, searchString: string): boolean {
		const tagsRegex = /tags\[(.*)\]/g;
		const match = tagsRegex.exec(searchString);
		let searchTags: string[] | undefined;

		if (match) {
			searchTags = match[1].split(',');
		}

		const searchText = searchString.replace(tagsRegex, '').toLowerCase();

		const hasAllSearchTags = !searchTags
			? true
			: DriveItemUtils.hasAllSearchTags(driveItem.tags, searchTags);

		if (!hasAllSearchTags) {
			return false;
		}

		const fieldsToCheck = [
			'label',
			'name',
			'descriptions',
			'tags',
			'titles'
		];

		for (let i = 0; i < fieldsToCheck.length; i++) {
			const field = fieldsToCheck[i];
			const value = driveItem[field];

			if (typeof(value) === 'object') {
				const subFields = Object.keys(value);

				for (let j = 0; j < subFields.length; j++) {
					const subField = subFields[j];
					if (value[subField].toString().toLowerCase().includes(searchText)) {
						return true;
					}
				}
			} else if (typeof(value) !== 'undefined') {
				if (value.toString().toLowerCase().includes(searchText)) {
					return true;
				}
			}
		}
		return false;
	}

	static hasAllSearchTags(driveItemTags: string[], searchTags: string[]): boolean {
		return searchTags.every(searchTag => driveItemTags.includes(searchTag));
	}

	static isDownloadableItemType(driveItem: Api.DriveItem): boolean {
		const { type } = driveItem;

		switch (type) {
			case Api.DriveItemType.File:
			case Api.DriveItemType.Image:
			case Api.DriveItemType.Movie:
			case Api.DriveItemType.Sound:
			case Api.DriveItemType.Component:
			case Api.DriveItemType.Template:
			case Api.DriveItemType.Publication:
				return true;
			case Api.DriveItemType.Directory:
			case Api.DriveItemType.V2Gateway:
			case Api.DriveItemType.Link:
			case Api.DriveItemType.AppContainer:
			case Api.DriveItemType.Gallery:
			case Api.DriveItemType.ExternalLink:
				return false;
		}
	}

	static isReadable(driveItem: Api.DriveItem, downloadManager: DownloadManagerState): boolean {
		const isOnline = UserAgentUtils.isOnline();
		const isPersistentlyDownloaded = DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id);

		let isReadable = false;
		switch (driveItem.type) {
			case Api.DriveItemType.Directory:
			case Api.DriveItemType.Image:
				isReadable = true;
				break;
			case Api.DriveItemType.ExternalLink:
				isReadable = isOnline;
				break;
			case Api.DriveItemType.Movie:
			case Api.DriveItemType.Sound:
			case Api.DriveItemType.Publication:
			case Api.DriveItemType.Component:
			case Api.DriveItemType.File:
				isReadable = isOnline || isPersistentlyDownloaded;
				break;
			default:
				break;
		}
		return isReadable;
	}

	static isDownloadable(driveItem: Api.DriveItem, downloadManager: DownloadManagerState, sharedMethods: Api.ShareMethod[] | undefined): boolean {
		const isDownloadableType = DriveItemUtils.isDownloadableItemType(driveItem);
		if (!isDownloadableType) {
			return false;
		}

		const isDesktop = UserAgentUtils.isDesktop();
		const isMobileApp = UserAgentUtils.isMobileApp;
		const isSafariMobile = UserAgentUtils.isSafari() && (!isDesktop && !isMobileApp);
		const isOnline = UserAgentUtils.isOnline();
		const hasDownloadRights = DriveItemUtils.hasDownloadRights(sharedMethods);
		const isPersistentlyDownloaded = DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id);

		let isDownloadable = false;
		switch (driveItem.type) {
			case Api.DriveItemType.File:
				isDownloadable = isOnline && hasDownloadRights && (!isMobileApp || !isPersistentlyDownloaded);
				break;
			case Api.DriveItemType.Image:
			case Api.DriveItemType.Movie:
			case Api.DriveItemType.Sound:
			case Api.DriveItemType.Publication:
				// Safari Mobile opens assets instead of downloading them
				isDownloadable = isOnline && hasDownloadRights && (!isMobileApp || !isPersistentlyDownloaded) && !isSafariMobile;
				break;
			case Api.DriveItemType.Component:
				isDownloadable = isOnline && hasDownloadRights && isMobileApp && !isPersistentlyDownloaded;
				break;
			default:
				break;
		}
		return isDownloadable;
	}

	static isUpdatable(
		driveItem: Api.DriveItem,
		downloadManager: DownloadManagerState,
		sharedMethods: Api.ShareMethod[] | undefined,
	): boolean {
		const isOnline = UserAgentUtils.isOnline();
		const hasDownloadRights = DriveItemUtils.hasDownloadRights(sharedMethods);
		const isPersistentlyDownloaded = DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id);
		let downloadedVersion = -1;

		if (isPersistentlyDownloaded) {
			downloadedVersion = downloadManager.items[driveItem.id].version;
		}

		const isUpdatable = isOnline
			&& hasDownloadRights
			&& isPersistentlyDownloaded
			&& (driveItem.version > downloadedVersion || this.hasUpdatableDependencies(driveItem, downloadManager));

		return isUpdatable;
	}

	static hasUpdatableDependencies(
		driveItem: Api.DriveItem,
		downloadManager: DownloadManagerState,
	): boolean {
		const itemFromDownloadManager = driveItem && downloadManager.items[driveItem.id] ? downloadManager.items[driveItem.id] : undefined;

		if (!itemFromDownloadManager) {
			return false;
		}

		const { dependencies } = driveItem;

		if (dependencies && dependencies.length > 0) {
			for (const dependency of dependencies) {
				const downloadedDependency = downloadManager.items[dependency.id];
				if (!downloadedDependency || dependency.version > downloadedDependency.version) {
					return true;
				}
			}
		}
		return false;
	}

	static isDeletable(driveItem: Api.DriveItem, downloadManager: DownloadManagerState): boolean {
		return DownloadManagerUtils.isRequestedItem(downloadManager, driveItem.id);
	}

	static hasDownloadRights(sharedMethods: Api.ShareMethod[] | undefined): boolean {
		return sharedMethods !== undefined && sharedMethods.includes(Api.ShareMethod.Download);
	}

	static isShareable(sharedMethods: Api.ShareMethod[] | undefined, galleryConfig: Api.Config): boolean {
		return sharedMethods !== undefined &&
			sharedMethods.includes(Api.ShareMethod.Share) &&
			!Utils.isMaster(galleryConfig);
	}

	static isPublication({ type }: Api.DriveItem): boolean {
		return type === Api.DriveItemType.Publication;
	}

	static isProposal(format?: Api.PublicationFormat): boolean {
		return !!format && [
			Api.PublicationFormat.PDF_EDITABLE,
			Api.PublicationFormat.EPUB_EDITABLE,
			Api.PublicationFormat.EPUB_MASTER,
		].includes(format);
	}

	static isMasterProposal(format?: Api.PublicationFormat): boolean {
		return format === Api.PublicationFormat.EPUB_MASTER;
	}

	static isDraftProposal(driveItem: Api.DriveItem): boolean {
		const { format, proposalStats } = driveItem;

		return DriveItemUtils.isProposal(format) &&
			!!proposalStats &&
			proposalStats.status === Api.ProposalStatus.DRAFT;
	}

	static isSentProposal(driveItem: Api.DriveItem): boolean {
		const { format, proposalStats } = driveItem;

		return DriveItemUtils.isProposal(format) &&
			!!proposalStats &&
			proposalStats.status === Api.ProposalStatus.SENT;
	}

	static isEditableProposal(driveItem: Api.DriveItem): boolean {
		const { format, proposalStats } = driveItem;
		const isProposal = DriveItemUtils.isProposal(format);
		return isProposal &&
			!!proposalStats &&
			proposalStats.editable &&
			!DriveItemUtils.isForbiddenForUpdateProposal(driveItem);
	}

	static isForbiddenForUpdateProposal(driveItem: Api.DriveItem): boolean {
		const { format, proposalStats } = driveItem;
		const isProposal = DriveItemUtils.isProposal(format);
		return isProposal && !!proposalStats && proposalStats.forbiddenUpdate;
	}

	static isMasterPublication(format?: Api.PublicationFormat): boolean {
		return format === Api.PublicationFormat.EPUB_PUBLI_MASTER;
	}

	static isModifiablePublication(format?: Api.PublicationFormat): boolean {
		return format === Api.PublicationFormat.EPUB_MODIFIABLE;
	}

	static isEditable(driveItem: Api.DriveItem, isAccountManagerOrAdmin: boolean, isPrivateDirDescendant?: boolean): boolean {
		const { format, componentType } = driveItem;
		const isPresentation = format === Api.PublicationFormat.EPUB_PRESENTATION;
		const isEditableComponent = componentType === Api.ComponentType.ZIP_EDITABLE;
		const isEpubPublication = !!format && format.includes(Api.PublicationFormat.EPUB);

		if (!isAccountManagerOrAdmin && isPrivateDirDescendant === false) {
			return false;
		}

		if (isAccountManagerOrAdmin && isEpubPublication) {
			return !DriveItemUtils.isProposal(format) ||
				DriveItemUtils.isMasterProposal(format) ||
				DriveItemUtils.isEditableProposal(driveItem);
		}

		return DriveItemUtils.isEditableProposal(driveItem)
			|| DriveItemUtils.isModifiablePublication(format)
			|| isPresentation
			|| isEditableComponent
			|| Utils.isEmbedMode();
	}

	static isUnread(driveList: DriveListState, readStatus: ReadItemsListState, id: number): boolean {
		const { driveItems } = driveList;
		const readItems = readStatus.items;
		return readItems[id] ? driveItems[id].version > readItems[id].lastVersionRead : true;
	}

	static isValidResourceForPublication({ type, name, componentType }: Api.DriveItem): boolean {
		const resourceTypes = [
			Api.DriveItemType.Image,
			Api.DriveItemType.Movie,
			Api.DriveItemType.Publication,
		];

		const fileExtension = name ? `.${Utils.getFileExtension(name)}` : '';

		// we have to set isValidFile to true to fetch content-block before we know 'name' of resource
		const isValidFile = type === Api.DriveItemType.File && (!name
			? true
			: DriveItemUtils.publicationExtensions.includes(fileExtension));

		// we have to set isValidComponent to true to fetch content-block before we know 'componentType' of resource
		const isValidComponent = type === Api.DriveItemType.Component && (!componentType
			? true
			: ![Api.ComponentType.ZIP, Api.ComponentType.ZIP_EDITABLE].includes(componentType));

		return resourceTypes.includes(type) || isValidFile || isValidComponent;
	}

	static isValidResourceForPlaylist({ format }: Api.DriveItem): boolean {
		return !!format && [PublicationFormat.EPUB, PublicationFormat.EPUB_PRESENTATION, PublicationFormat.EPUB_PUBLI_MASTER, PublicationFormat.EPUB_MODIFIABLE].includes(format);
	}

	static convertDisplayTypeFormat(aspDisplayType: Api.AspFolderDisplayType): ListDisplayType {
		switch (aspDisplayType) {
			case Api.AspFolderDisplayType.List:
				return ListDisplayType.Row;
			case Api.AspFolderDisplayType.Masonry:
				return ListDisplayType.Masonry;
			case Api.AspFolderDisplayType.Coverflow:
				return ListDisplayType.Coverflow;
			case Api.AspFolderDisplayType.Grid:
			default:
				return ListDisplayType.Grid;
		}
	}

	static isDirDescendantOf(folderId: number | undefined, ancestorDirId: number | undefined, driveItems: DriveItemInfoMap): boolean {
		if (!folderId || !ancestorDirId || !driveItems[folderId]) {
			return false;
		}

		if (folderId === ancestorDirId) {
			return true;
		}

		const parentId = DriveItemUtils.getParentFolderId(driveItems[folderId]);
		return DriveItemUtils.isDirDescendantOf(parentId, ancestorDirId, driveItems);
	}

	static getDriveItemChildren(driveItemId: number): number[] {
		const { driveList: {driveItems} } = store.getState();
		const driveItem = driveItems[driveItemId];

		let children = [driveItemId];
		if (driveItem.resources) {
			children = children.concat(...driveItem.resources.map(resource => DriveItemUtils.getDriveItemChildren(resource.id)));
		}

		return children;
	}

	static getDisplayType = (driveList: DriveListState, routerParamsHistory: RouterParamsState[], folderId?: number | string): ListDisplayType => {
		const {
			foldersDisplayType,
			displayType: storeDisplayType,
		} = driveList;
		const {
			galleryInfos: {config : {applicationGallery}},
			driveList: {driveItems}
		} = store.getState();

		// handle displayType of New, Downloads, Selection and Search folders
		const folderIdOrOrigin = folderId || RouteUtils.getCurrentOrigin(routerParamsHistory);

		// high priority: displayMode which is set inside folder via displayType dropdown
		if (foldersDisplayType[folderIdOrOrigin]) {
			return foldersDisplayType[folderIdOrOrigin];
		}

		// middle priority: displayMode which is set in Parameters page (except Default)
		if (storeDisplayType !== ListDisplayType.Default) {
			return storeDisplayType;
		}

		// low priority: displayMode which is set in backoffice
		if (driveList.driveItems[folderIdOrOrigin]) {
			const { displayMode: aspDisplayType } = driveList.driveItems[folderIdOrOrigin];
			if (aspDisplayType) {
				return DriveItemUtils.convertDisplayTypeFormat(aspDisplayType);
			}
		}

		// low priority: use root folder displayMode if no other override is set
		if (driveItems[applicationGallery] && driveItems[applicationGallery].displayMode) {
			const displayMode = (driveItems[applicationGallery].displayMode as Api.AspFolderDisplayType);
			return DriveItemUtils.convertDisplayTypeFormat(displayMode);
		}

		// fallback
		return ListDisplayType.Grid;
	}

	static getContextualMenuButtons = (driveItem: DriveItemInfo | undefined, state: StoreState): ContextualMenuButtons => {
		let isDownloadable = false;
		let isDownloading = false;
		let isDownloadInProgress = false;
		let hasDownloadErrors = false;
		let isUpdatable = false;
		let isDeletable = false;
		let isAvailableOffline = false;
		let isShareable = false;
		let isEditable = false;
		let isPublicationCopyable = false;
		let isProposalCopyable = false;
		let isProposalNotCopyable = false;
		let isProposalArchivable = false;
		let isRenamable = false;
		let isDeletableFromAsp = false;
		let isCompatibleForTemplate = false;
		let isCustomTemplate = false;
		let isCoverImageEditable = false;
		let isCheckForUpdatePossible = false;
		let isCopyableAsProposal = false;

		const {
			driveList,
			driveList: { driveItems },
			publicationCreator: {
				fromTemplate: { customTemplatesIds },
			},
			downloadManager,
			galleryInfos: {
				config,
				template: {
					sharedMethods,
					contextMenuOptions: {
						alwaysHidden = [ContextualMenuItemType.CreateProposal, ContextualMenuItemType.CreateCustomizedProposal],
					} = {},
				},
			},
			menuManager: { menuModes },
			profile,
		} = state;

		const isAccountManagerOrAdmin = getIsAccountManagerOrAdmin(state);
		const privateDirId = Utils.getPrivateDirId(driveList, config);
		const archiveDirId = Utils.getArchiveDirId(driveList, config);
		const sourceDirId = Utils.getSourcesDirId(driveList, config);
		const sharedDirId = Utils.getSharedDirId(driveList, config);

		// needed in the case an asset is deletedFromAsp while context menu is opened
		if (driveItem) {
			isCustomTemplate = customTemplatesIds.includes(driveItem.id);

			if (isCustomTemplate) {
				return {
					[ContextualMenuItemType.RemoveTemplate]: true,
				} as ContextualMenuButtons;
			}

			isDownloading = DownloadManagerUtils.isItemDownloading(downloadManager, driveItem.id);
			isDownloadInProgress = DownloadManagerUtils.isItemDownloadingInProgress(downloadManager, driveItem.id);
			isDownloadable = DriveItemUtils.isDownloadable(driveItem, downloadManager, sharedMethods) && !isDownloadInProgress;
			isUpdatable = DriveItemUtils.isUpdatable(driveItem, downloadManager, sharedMethods);
			isDeletable = DriveItemUtils.isDeletable(driveItem, downloadManager);
			hasDownloadErrors = DownloadManagerUtils.hasDownloadErrors(downloadManager, driveItem.id);
			isAvailableOffline = isDeletable && !hasDownloadErrors && !isDownloading;
			isShareable = DriveItemUtils.isShareable(sharedMethods, config);
			isCheckForUpdatePossible = UserAgentUtils.isMobileApp;

			if (Utils.isReadOnlyApp(profile.role)) {
				return {
					[ContextualMenuItemType.Download]: isDownloadable,
					[ContextualMenuItemType.Update]: isUpdatable,
					[ContextualMenuItemType.Delete]: isAvailableOffline,
					[ContextualMenuItemType.Share]: isShareable,
					[ContextualMenuItemType.CheckForUpdate]: isCheckForUpdatePossible,
				} as ContextualMenuButtons;
			}

			const isPrivateDirDescendant = DriveItemUtils.isDirDescendantOf(driveItem.id, privateDirId, driveItems);
			const isArchiveDirDescendant = DriveItemUtils.isDirDescendantOf(driveItem.id, archiveDirId, driveItems);
			isEditable = DriveItemUtils.isEditable(driveItem, isAccountManagerOrAdmin, isPrivateDirDescendant);
			const isPublication = DriveItemUtils.isPublication(driveItem);
			const isProposal = DriveItemUtils.isProposal(driveItem.format);
			const isDraftProposal = DriveItemUtils.isDraftProposal(driveItem);
			const isSentProposal = DriveItemUtils.isSentProposal(driveItem);
			const isMasterProposal = DriveItemUtils.isMasterProposal(driveItem.format);
			const isMasterPublication = DriveItemUtils.isMasterPublication(driveItem.format);
			const isDeletableFromAspAssetType = !(isProposal || isMasterPublication) ||
				isDraftProposal ||
				isSentProposal ||
				DriveItemUtils.isModifiablePublication(driveItem.format);
			const hasTemplatesOptionInCreateBtn = Object.values(menuModes).some(menuPosition => (
				menuPosition.some((menuItem: MenuItem) => (
					menuItem.type === MenuItemType.CreateButton &&
					menuItem.options!.includes(CreateButtonOption.publicationFromTemplate)
				))
			));
			const isSystemFolder = [sourceDirId, sharedDirId].includes(driveItem.id);

			isPublicationCopyable = isMasterPublication ||
				(isAccountManagerOrAdmin && DriveItemUtils.isModifiablePublication(driveItem.format));
			isProposalCopyable = isProposal && !DriveItemUtils.isForbiddenForUpdateProposal(driveItem);
			isProposalNotCopyable = isProposal && DriveItemUtils.isForbiddenForUpdateProposal(driveItem);
			isProposalArchivable = !!archiveDirId &&
				(isAccountManagerOrAdmin || isPrivateDirDescendant) &&
				!isArchiveDirDescendant &&
				isProposal && !isDraftProposal && !isSentProposal && !isMasterProposal;
			isRenamable = !isSystemFolder &&
				(isAccountManagerOrAdmin || isPrivateDirDescendant || isArchiveDirDescendant);
			isDeletableFromAsp = isDeletableFromAspAssetType &&
				(isAccountManagerOrAdmin || isPrivateDirDescendant) &&
				!isSystemFolder;
			isCompatibleForTemplate = hasTemplatesOptionInCreateBtn && DriveItemUtils.isValidResourceForPublication(driveItem);
			isCoverImageEditable = isAccountManagerOrAdmin || isPrivateDirDescendant;
			isCopyableAsProposal = isPublication;
		}

		return DriveItemUtils.hideContextualMenuOptions({
			[ContextualMenuItemType.Download]: isDownloadable,
			[ContextualMenuItemType.Update]: isUpdatable,
			[ContextualMenuItemType.Delete]: isAvailableOffline,
			[ContextualMenuItemType.Share]: isShareable,
			[ContextualMenuItemType.ShareWithUser]: isEditable,
			[ContextualMenuItemType.Edit]: isEditable,
			[ContextualMenuItemType.PublicationSaveAs]: isPublicationCopyable || isProposalCopyable,
			[ContextualMenuItemType.ProposalNoSaveAs]: isProposalNotCopyable,
			[ContextualMenuItemType.Rename]: isRenamable,
			[ContextualMenuItemType.DeleteFromAsp]: isDeletableFromAsp,
			[ContextualMenuItemType.Archive]: isProposalArchivable,
			[ContextualMenuItemType.SetAsTemplate]: isCompatibleForTemplate,
			[ContextualMenuItemType.EditCoverImage]: isCoverImageEditable,
			[ContextualMenuItemType.CheckForUpdate]: isCheckForUpdatePossible,
			[ContextualMenuItemType.CreateProposal]: isCopyableAsProposal,
			[ContextualMenuItemType.CreateCustomizedProposal]: isCopyableAsProposal,
		} as ContextualMenuButtons, alwaysHidden);
	}

	static hideContextualMenuOptions = (contextualMenuButtons: ContextualMenuButtons, hiddenOptions: ContextualMenuItemType[]): ContextualMenuButtons => {
		const newContextualMenuButtons = { ...contextualMenuButtons };
		hiddenOptions.forEach(hiddenOption => {
			newContextualMenuButtons[hiddenOption] = false;
		});

		return newContextualMenuButtons;
	}

	static isEmptyContextualMenu = (driveItem: DriveItemInfo, state: StoreState): boolean => {
		return Object.values(DriveItemUtils.getContextualMenuButtons(driveItem, state)).every(button => !button);
	}

	static getParentFolderId = ({ linkParentId, parent }: Api.DriveItem): number => {
		return linkParentId || parent;
	}

	static getRootDirIdForEditMode = (state: StoreState): number | undefined => {
		const {
			galleryInfos: { config },
			driveList,
		} = state;
		const isAccountManagerOrAdmin = getIsAccountManagerOrAdmin(state);
		const privateDirId = Utils.getPrivateDirId(driveList, config);

		return isAccountManagerOrAdmin ? config.applicationGallery : privateDirId;
	}

	static sanitizeForCSS = (name: string): string => {
		return name.replace(/[^a-zA-Z0-9]/g, (s) => {
			switch (s) {
				case ' ':
					return '-';
				case '/':
					return '-or-';
				case '~':
					return '_';
				default:
					return s.charCodeAt(0).toString();
			}
		});
	}

	static getItemClassName = (mainClassName: string, driveItem?: Api.DriveItem, tagClasses?: string[]): string => {
		if (!driveItem) {
			return mainClassName;
		}

		let className: string = `${mainClassName} itemType-${driveItem.type}`;

		if (driveItem.id) {
			className += ` itemId-${driveItem.id}`;
		}

		if (driveItem.tags) {
			for (const tag of driveItem.tags) {
				if (tagClasses && tagClasses.includes(tag)) {
					className += ` itemTag-${DriveItemUtils.sanitizeForCSS(tag)}`;
				}
			}
		}

		return className;
	}
}

export {
	ResolutionDisplayType,
	ActionButton,
	ActionButtonStyle,
	ThumbnailDescriptor,
};
export default DriveItemUtils;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/driveItem.ts